import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useIncidents,
  useApproveIncident,
  useDenyIncident,
  useCloseIncident,
  useResumeLocation,
  type QueuedCompensation,
} from '@/api/hooks/useAdminIncidents';
import { useRestoreUnit } from '@/api/hooks/useAdminUnits';
import { formatDateTime } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';

/** Tick every second so countdowns re-render; returns `now` in ms. */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Counts down to the server-provided absolute auto-approve instant — never re-derives the timeout. */
function AutoApproveCountdown({ deadlineIso }: { deadlineIso: string }) {
  const { t } = useTranslation();
  const now = useNow();
  const remainingMs = new Date(deadlineIso).getTime() - now;
  if (Number.isNaN(remainingMs)) return null;
  if (remainingMs <= 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Clock className="h-3 w-3" /> {t('admin.incident_auto_approving')}
      </span>
    );
  }
  const totalSec = Math.floor(remainingMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = String(totalSec % 60).padStart(2, '0');
  const urgent = remainingMs < 60_000;
  return (
    <span
      className={
        'inline-flex items-center gap-1 text-xs font-semibold tabular-nums ' +
        (urgent ? 'text-destructive' : 'text-[--color-brand]')
      }
    >
      <Clock className="h-3 w-3" />
      {t('admin.incident_auto_approve_in', { time: `${mm}:${ss}` })}
    </span>
  );
}

function money(action: string, refund: string | null, credit: string | null): string {
  const parts: string[] = [];
  if (refund && Number(refund) > 0) parts.push(`$${refund} refund`);
  if (credit && Number(credit) > 0) parts.push(`$${credit} credit`);
  return parts.length ? parts.join(' + ') : action;
}

export default function IncidentsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useIncidents();
  const approveMutation = useApproveIncident();
  const denyMutation = useDenyIncident();
  const closeMutation = useCloseIncident();
  const restoreMutation = useRestoreUnit();
  const resumeMutation = useResumeLocation();

  const [denyTarget, setDenyTarget] = useState<QueuedCompensation | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const handleApprove = async (outageId: string) => {
    try {
      await approveMutation.mutateAsync(outageId);
      toast.success(t('admin.incident_approved_toast'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeny = async () => {
    if (!denyTarget || denyReason.trim().length < 3) return;
    try {
      await denyMutation.mutateAsync({ outageId: denyTarget.outageId, reason: denyReason.trim() });
      toast.success(t('admin.incident_denied_toast'));
      setDenyTarget(null);
      setDenyReason('');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleClose = async (outageId: string) => {
    try {
      await closeMutation.mutateAsync(outageId);
      toast.success(t('admin.incident_closed_toast'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleRestore = async (unitId: string) => {
    try {
      await restoreMutation.mutateAsync(unitId);
      toast.success(t('admin.incident_restored_toast'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleResume = async (locationId: string) => {
    try {
      await resumeMutation.mutateAsync(locationId);
      toast.success(t('admin.incident_resumed_toast'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">{t('admin.incidents')}</h1>
        <Card>
          <CardContent className="space-y-2 p-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">{t('admin.incidents')}</h1>
        <Card>
          <CardContent className="p-6">
            <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {t('common.error')}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { suspendedUnits, pausedLocations, queuedCompensations, pendingManualResolution } = data;
  const nothing =
    suspendedUnits.length === 0 &&
    pausedLocations.length === 0 &&
    queuedCompensations.length === 0 &&
    pendingManualResolution.length === 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('admin.incidents')}</h1>

      {/* Reassuring empty state — the screen most mornings: the platform handled the night. */}
      {nothing && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-sm font-medium">{t('admin.incident_none_title')}</p>
            <p className="text-xs text-muted-foreground">{t('admin.incident_none_body')}</p>
          </CardContent>
        </Card>
      )}

      {/* 1. Pending approval — time-sensitive, top. Only present when approval mode is ON. */}
      {queuedCompensations.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[--color-brand]">
            <ShieldAlert className="h-4 w-4" /> {t('admin.incident_pending_approval')}
          </h2>
          <Card className="border-[--color-brand]/40">
            <CardContent className="divide-y p-0">
              {queuedCompensations.map((q) => (
                <div key={q.outageId} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-[200px] space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[--color-brand] text-white">{q.decisionAction}</Badge>
                      <span className="text-sm font-semibold">
                        {money(q.decisionAction, q.refundAmount, q.creditAmount)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('admin.incident_booking')}: {q.bookingId ?? '—'} · {t('admin.incident_queued')} {formatDateTime(q.queuedAt, i18n.language)}
                    </div>
                    <AutoApproveCountdown deadlineIso={q.autoApproveDeadline} />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(q.outageId)}
                      disabled={approveMutation.isPending}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" /> {t('admin.incident_approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => { setDenyTarget(q); setDenyReason(''); }}
                      disabled={denyMutation.isPending}
                    >
                      <XCircle className="mr-1 h-3 w-3" /> {t('admin.incident_deny')}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* 2. Pending manual resolution — denied comps the operator must handle. */}
      {pendingManualResolution.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-600">
            <AlertTriangle className="h-4 w-4" /> {t('admin.incident_pending_manual')}
          </h2>
          <Card>
            <CardContent className="divide-y p-0">
              {pendingManualResolution.map((p) => (
                <div key={p.outageId} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-[200px] space-y-1">
                    <div className="text-sm">
                      <span className="font-medium">{t('admin.incident_denied_label')}</span>{' '}
                      <Badge variant="secondary">{p.decisionAction}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('admin.incident_booking')}: {p.bookingId ?? '—'}
                      {p.deniedAt && <> · {formatDateTime(p.deniedAt, i18n.language)}</>}
                    </div>
                    {p.reason && (
                      <div className="text-xs text-foreground">
                        {t('admin.incident_reason')}: <span className="italic">{p.reason}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleClose(p.outageId)}
                    disabled={closeMutation.isPending}
                  >
                    {t('admin.incident_close')}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* 3. Active incidents — what the platform did + diagnostics. */}
      {(suspendedUnits.length > 0 || pausedLocations.length > 0) && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-destructive" /> {t('admin.incident_active')}
          </h2>
          <Card>
            <CardContent className="divide-y p-0">
              {pausedLocations.map((l) => (
                <div key={l.locationId} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex items-center gap-3">
                    <PauseCircle className="h-5 w-5 text-destructive" />
                    <div>
                      <div className="text-sm font-medium">{l.locationName}</div>
                      <div className="text-xs text-muted-foreground">{t('admin.incident_location_paused')}</div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleResume(l.locationId)}
                    disabled={resumeMutation.isPending}
                  >
                    <PlayCircle className="mr-1 h-3 w-3" /> {t('admin.incident_resume')}
                  </Button>
                </div>
              ))}
              {suspendedUnits.map((u) => (
                <div key={u.unitId} className="flex flex-wrap items-start justify-between gap-3 p-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="mt-0.5 h-5 w-5 text-destructive" />
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {u.unitName} <Badge variant="secondary" className="ml-1">{t('admin.incident_offline')}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {u.suspendedAt && <>{t('admin.incident_suspended_at')} {formatDateTime(u.suspendedAt, i18n.language)}</>}
                      </div>
                      {u.lastFailures.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
                          {u.lastFailures.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(u.unitId)}
                    disabled={restoreMutation.isPending}
                  >
                    <RotateCcw className="mr-1 h-3 w-3" /> {t('admin.incident_restore')}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Deny dialog — reason required (Gate 3c). */}
      <Dialog open={!!denyTarget} onOpenChange={(open) => { if (!open) { setDenyTarget(null); setDenyReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.incident_deny_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('admin.incident_deny_body')}</p>
            <Label htmlFor="denyReason">{t('admin.incident_deny_reason')}</Label>
            <Textarea
              id="denyReason"
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder={t('admin.incident_deny_reason_placeholder')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDenyTarget(null); setDenyReason(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeny}
              disabled={denyReason.trim().length < 3 || denyMutation.isPending}
            >
              {t('admin.incident_deny')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
